import { GROUP_TYPE, IFileAndCaption } from "@home-health-notify/domain";
import { Box } from "@mui/material";
import {
  ApiErrorHandler,
  ChatContext,
  getChatDeletedMessage,
  isChatDeletedError,
  useFetchChats,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { ROUTES } from "../routes";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { useFileUpload } from "../hooks/useFileUpload";
import MessagePanelContainer from "../message/MessagePanelContainer";
import FileUploader from "../uploader/FileUploader";

export const RightPanel = () => {
  const params = useParams();
  const navigate = useNavigate();
  const chatId = params?.chatId || "";
  const { enqueueSnackbar } = useSnackbar();
  const groupTypeParam = params?.groupType?.toUpperCase() as GROUP_TYPE;
  const isValidGroupType = Object.values(GROUP_TYPE).includes(groupTypeParam);
  const { fetchChatError } = useFetchChats(
    isValidGroupType ? chatId : "",
    groupTypeParam,
  );
  const { chat } = useContext(ChatContext);

  useEffect(() => {
    if (!fetchChatError) return;
    if (isChatDeletedError(fetchChatError)) {
      // The chat stays open: its info loaded fine and only the messages are
      // gone, so navigating away would blank a panel the user can still read.
      enqueueSnackbar(getChatDeletedMessage(chat?.chatInfo?.name), {
        variant: "error",
        autoHideDuration: 3000,
      });
      return;
    }
    ApiErrorHandler(fetchChatError, enqueueSnackbar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchChatError]);

  useEffect(() => {
    const groupTypes = Object.values(GROUP_TYPE);
    if (!groupTypes.includes(groupTypeParam)) {
      console.error(
        `Invalid groupType "${params?.groupType}" in URL: ${window.location.pathname}`,
      );
      navigate(`../${ROUTES.CHATS}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupTypeParam]);

  const { t } = useTranslation();

  const {
    mediaDrafts,
    getRootProps,
    isDragActive,
    isDropAccepted,
    fileProgressStatus,
    openFileDialog,
    onRemoveFile,
    previewDraft,
    onSetPreview,
    onClosePreview,
    onUpload,
    isUploadFailed,
    onSetCaption,
    isThumbnailCreated,
    resetThumbnailFlag,
    getInputProps,
    isProcessingFiles,
    pasteFiles,
    setDropAccepted,
    showBulkUploadConfirm,
    pendingFilesCount,
    onConfirmBulkUpload,
    onCancelBulkUpload,
  } = useFileUpload(chat!);

  // Call getRootProps() inside render
  const rootProps = getRootProps();

  const onPasteFiles = async (fileList: IFileAndCaption[]) => {
    try {
      await pasteFiles(fileList);
      setDropAccepted(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      enqueueSnackbar(message, {
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <Box
      data-testid="drop-input"
      {...rootProps}
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          minWidth: 0,
          position: "relative",
        },
        ...(Array.isArray(rootProps.sx) ? rootProps.sx : [rootProps.sx]),
      ]}
    >
      {chat && chat.chatInfo.id === chatId && (
        <>
          <input {...getInputProps()} />
          <MessagePanelContainer
            key={chat.getChatId()} // Ensures component re-mounts when chat changes so the component initializes for every chat – use chatId from the chat object, not from route params
            isUploadFailed={isUploadFailed}
            openFileDialog={openFileDialog}
            fileProgressStatus={fileProgressStatus}
            onPasteFile={onPasteFiles}
          />
          <ConfirmationDialog
            isOpen={showBulkUploadConfirm}
            onClose={onCancelBulkUpload}
            confirmationTitle={t("preview.bulkUploadConfirmTitle", {
              ns: "message",
            })}
            confirmationMessage={t("preview.bulkUploadConfirmMessage", {
              ns: "message",
              count: pendingFilesCount,
            })}
            confirmationButtonText={t("preview.bulkUploadConfirmButton", {
              ns: "message",
            })}
            onConfirmationAction={onConfirmBulkUpload}
          />
          {(isDragActive || isDropAccepted) && (
            <FileUploader
              mediaDrafts={mediaDrafts}
              openFileDialog={openFileDialog}
              onRemoveFile={onRemoveFile}
              previewDraft={previewDraft}
              onSetPreview={onSetPreview}
              closePreview={onClosePreview}
              isDragActive={isDragActive}
              isDropAccepted={isDropAccepted}
              upload={onUpload}
              onSetCaption={onSetCaption}
              isThumbnailCreated={isThumbnailCreated}
              resetThumbnailFlag={resetThumbnailFlag}
              isProcessingFiles={isProcessingFiles}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default RightPanel;
